import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";import MapView from "@/components/MapView";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History as HistoryIcon, Route as RouteIcon, Gauge, Flag, PlayCircle, UserCheck, UserX, AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const EVENT_STYLE = {
  depart: { icon: PlayCircle, color: "text-blue-600" },
  picked_up: { icon: UserCheck, color: "text-emerald-600" },
  absent: { icon: UserX, color: "text-amber-600" },
  exces_vitesse: { icon: Gauge, color: "text-red-600" },
  fin: { icon: Flag, color: "text-slate-600" },
};

export default function History() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = (d) => api.get("/dispatch/history", { params: { date: d } })
    .then((r) => { setRows(r.data); setDetail(null); })
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(date); }, [date]); // eslint-disable-line

  const open = (id) => api.get(`/dispatch/history/${id}`).then((r) => setDetail(r.data))
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));

  return (
    <>
      <PageHeader title="Historique des routes" icon={HistoryIcon}
        subtitle="Tracé parcouru, heures de passage réelles et événements de la journée"
        actions={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-44" data-testid="history-date" />} />

      <div className="p-8 grid lg:grid-cols-[380px_1fr] gap-6 items-start">
        <div className="space-y-2" data-testid="history-list">
          {!rows.length && <div className="text-sm text-muted-foreground border border-border rounded-lg p-8 text-center">
            Aucune route pour cette date.
          </div>}
          {rows.map((r, i) => (
            <button key={r.assignment_id} onClick={() => open(r.assignment_id)}
              data-testid={`history-row-${r.route_number}`}
              className={`w-full text-left rounded-lg border p-4 transition-colors ${
                detail?.assignment_id === r.assignment_id ? "border-primary bg-secondary/60" : "border-border hover:bg-secondary/40"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-display font-bold">Route {r.route_number}</span>
                <div className="flex items-center gap-1.5">
                  <Badge className={r.period === "AM" ? "bg-blue-500" : "bg-indigo-500"}>{r.period}</Badge>
                  <Badge variant={r.status === "completed" ? "secondary" : "default"}>
                    {r.status === "completed" ? "Terminée" : r.status === "in_progress" ? "En cours" : "Non partie"}
                  </Badge>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
              <div className="text-sm text-muted-foreground mt-1">{r.driver_name || "—"} · autobus {r.bus_fleet || "—"}</div>
              <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap gap-x-3">
                <span>{r.started_at || "—"} → {r.completed_at || "—"}</span>
                <span>{r.stops_done}/{r.stops_total} {r.period === "PM" ? "débarqués" : "embarqués"}</span>
                {r.stops_absent > 0 && <span className="text-amber-600">{r.stops_absent} absents</span>}
                {r.speed_events > 0 && <span className="text-red-600 font-semibold">{r.speed_events} excès</span>}
              </div>
            </button>
          ))}
        </div>

        {!detail ? (
          <div className="border border-border rounded-lg h-[60vh] flex items-center justify-center text-muted-foreground text-sm">
            Choisissez une route pour voir le tracé parcouru.
          </div>
        ) : (
          <div className="space-y-4" data-testid="history-detail">
            <div className="grid sm:grid-cols-4 gap-3">
              {[["Distance parcourue", `${detail.distance_km} km`],
                ["Durée", `${detail.started_at || "—"} → ${detail.completed_at || "—"}`],
                ["Arrêts", `${detail.stops.filter((s) => s.progress_status === "PICKED_UP").length}/${detail.stops.length}`],
                ["Points GPS", detail.track.length]].map(([k, v]) => (
                <div key={k} className="border border-border rounded-lg p-3">
                  <div className="overline">{k}</div>
                  <div className="font-display text-xl font-bold mt-1">{v}</div>
                </div>
              ))}
            </div>

            <div className="border border-border rounded-lg overflow-hidden h-[46vh]">
              <MapView stops={detail.stops} garage={detail.garage} school={detail.school}
                geometry={detail.track.map((p) => [p.lat, p.lng])} polylineColor="#0EA5E9" />
            </div>

            <div className="border border-border rounded-lg divide-y divide-border" data-testid="history-events">
              <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <RouteIcon className="w-4 h-4" /> Déroulement de la route
              </div>
              {!detail.events.length && <div className="px-4 py-6 text-sm text-muted-foreground text-center">Aucun événement enregistré.</div>}
              {detail.events.map((e, i) => {
                const st = EVENT_STYLE[e.type] || { icon: AlertTriangle, color: "text-slate-500" };
                const Icon = st.icon;
                return (
                  <div key={i} className="px-4 py-2.5 flex items-start gap-3 text-sm" data-testid={`history-event-${i}`}>
                    <span className="font-mono text-xs text-muted-foreground w-12 shrink-0 pt-0.5">{e.at || "—"}</span>
                    <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${st.color}`} />
                    <span className="min-w-0">{e.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="border border-border rounded-lg divide-y divide-border">
              <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Heures de passage réelles
              </div>
              {detail.stops.map((s, i) => (
                <div key={s.id} className="px-4 py-2 grid grid-cols-12 gap-2 text-sm items-center" data-testid={`history-stop-${i}`}>
                  <span className="col-span-1 text-muted-foreground">#{i + 1}</span>
                  <span className="col-span-5 truncate">{s.address}</span>
                  <span className="col-span-2 text-muted-foreground text-xs">prévu {s.pickup_time || "—"}</span>
                  <span className="col-span-2 font-mono text-xs">réel {s.done_at || s.arrived_at || "—"}</span>
                  <span className="col-span-2 text-right">
                    <Badge variant="secondary" className="text-[10px]">{s.progress_status}</Badge>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
